import { createContext, type ReactNode, useContext } from "react";

import type { IBTLinkRouting } from "./link.types";

/**
 * Ambient routing for {@link BTLink}. `null` when no provider is present, so a
 * link falls back to its explicit `routing` prop.
 */
const BTLinkRoutingContext = createContext<IBTLinkRouting | null>(null);

/**
 * Supplies the app-specific routing that core {@link BTLink} needs, so consumers
 * don't have to thread `routing` through every call site. Core defines the
 * context; the host app owns the value it feeds in (keeping core free of any
 * static import of the app's routing subsystem). Analytics is not supplied here
 * — BTLink self-reports link clicks through BDS tracking.
 *
 * `isSpa` varies by position in the app tree (it's set per-layout), so render
 * this provider wherever that value is established rather than only at the root.
 * A link's explicit `routing` prop still wins over the context value.
 */
export function BTLinkRoutingProvider({
  value,
  children,
}: {
  value: IBTLinkRouting;
  children: ReactNode;
}) {
  return (
    <BTLinkRoutingContext.Provider value={value}>
      {children}
    </BTLinkRoutingContext.Provider>
  );
}

/**
 * Reads the ambient {@link IBTLinkRouting} supplied by
 * {@link BTLinkRoutingProvider}, or `null` when no provider is present. BTLink
 * uses this as the fallback for its `routing` prop.
 */
export function useBTLinkRouting(): IBTLinkRouting | null {
  return useContext(BTLinkRoutingContext);
}
